import React from "react";
import { DispatchProp } from "react-redux";
import { BaseControl, ControlProps } from "./BaseControl";
import { getRendererProps } from "../renderers/BaseRenderer";
import { ChoiceModel } from "../../models/ChoiceModel";
import NetPromoterRenderer from "../renderers/v2/NetPromoterRenderer";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import {
    NpsFeedbackQuestionSettings,
    NpsScoresBySegment,
} from "../../models/enums/NpsFeedbackQuestionSettings";
import {
    getSettingBoolean,
    getSettingString,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { isNullOrEmpty } from "../../utils";

interface Props extends ControlProps, DispatchProp {}
interface State {
    feedbackQuestion: string;
    feedbackVariable: string;
    feedbackAnswerRequired: boolean;
    showFeedbackQuestion: boolean;
    openEndValue: string;
    oldFeedbackVariable: string;
}
export class NetPromoterControl extends BaseControl<Props, State> {
    constructor(props: Props) {
        super(props);
        const { question } = this.props;
        this.state = {
            ...this.generateNpsFeedbackQuestionObject(
                NpsFeedbackQuestionSettings.DefaultFeedbackQuestion,
                getSettingString(question, NpsFeedbackQuestionSettings.FeedbackVariable.toString()),
                getSettingBoolean(
                    question,
                    NpsFeedbackQuestionSettings.FeedbackAnswerRequired.toString(),
                ),
            ),
            showFeedbackQuestion: false,
            openEndValue: "",
            oldFeedbackVariable: "",
        };
    }

    onSelect = (choice: ChoiceModel) => {
        const [variable] = this.props.sequencedVariables;
        this.setMentions(this.props, variable.name, (mentions) => mentions.set(choice.value));
        const { question } = this.props;
        this.setState({
            oldFeedbackVariable: this.state.feedbackVariable,
        });
        let npsFeedbackQuestion;
        if (
            hasSetting(question, NpsFeedbackQuestionSettings.HasFeedbackQuestion) &&
            getSettingBoolean(question, NpsFeedbackQuestionSettings.HasFeedbackQuestion)
        ) {
            if (hasSetting(question, NpsFeedbackQuestionSettings.FeedbackVariable.toString())) {
                npsFeedbackQuestion = this.generateNpsFeedbackQuestionObject(
                    NpsFeedbackQuestionSettings.DefaultFeedbackQuestion,
                    getSettingString(question, NpsFeedbackQuestionSettings.FeedbackVariable),
                    getSettingBoolean(question, NpsFeedbackQuestionSettings.FeedbackAnswerRequired),
                );
            }
            if (
                Number(choice.value) >= NpsScoresBySegment.PromotersScore &&
                hasSetting(question, NpsFeedbackQuestionSettings.PromotersVariable.toString())
            ) {
                npsFeedbackQuestion = this.generateNpsFeedbackQuestionObject(
                    NpsFeedbackQuestionSettings.PromotersFeedbackQuestion,
                    getSettingString(question, NpsFeedbackQuestionSettings.PromotersVariable),
                    getSettingBoolean(
                        question,
                        NpsFeedbackQuestionSettings.PromotersAnswerRequired,
                    ),
                );
            }
            if (
                Number(choice.value) < NpsScoresBySegment.PromotersScore &&
                Number(choice.value) > NpsScoresBySegment.DetractorsScore &&
                hasSetting(question, NpsFeedbackQuestionSettings.PassivesVariable.toString())
            ) {
                npsFeedbackQuestion = this.generateNpsFeedbackQuestionObject(
                    NpsFeedbackQuestionSettings.PassivesFeedbackQuestion,
                    getSettingString(question, NpsFeedbackQuestionSettings.PassivesVariable),
                    getSettingBoolean(question, NpsFeedbackQuestionSettings.PassivesAnswerRequired),
                );
            }
            if (
                Number(choice.value) <= NpsScoresBySegment.DetractorsScore &&
                hasSetting(question, NpsFeedbackQuestionSettings.DetractorsVariable.toString())
            ) {
                npsFeedbackQuestion = this.generateNpsFeedbackQuestionObject(
                    NpsFeedbackQuestionSettings.DetractorsFeedbackQuestion,
                    getSettingString(question, NpsFeedbackQuestionSettings.DetractorsVariable),
                    getSettingBoolean(
                        question,
                        NpsFeedbackQuestionSettings.DetractorsAnswerRequired,
                    ),
                );
            }
            this.setState({
                ...npsFeedbackQuestion,
                showFeedbackQuestion: true,
            });
            this.renderQuestion();
        }
    };

    onOpenEndChange = (event: any) => {
        const { value } = event.target;
        this.setState({
            openEndValue: value,
        });
    };

    onOpenEndBlur = (): void => {
        const openEndName = this.state.feedbackVariable;
        const openEnd = this.state.openEndValue;
        const openEndAnswerCode = "0";
        if (this.state.oldFeedbackVariable !== undefined) {
            this.setMentions(this.props, this.state.oldFeedbackVariable, (mentions) =>
                mentions.clear(),
            );
        }
        this.setMentions(this.props, openEndName, (mentions) => mentions.clear());
        if (!isNullOrEmpty(openEnd)) {
            this.setMentions(this.props, openEndName, (mentions) =>
                mentions.set(openEndAnswerCode, openEnd.trim()),
            );
        }
    };

    generateNpsFeedbackQuestionObject(
        feedbackQuestion: string,
        feedbackVariable: string,
        feedbackAnswerRequired: boolean,
    ) {
        return {
            feedbackQuestion,
            feedbackVariable,
            feedbackAnswerRequired,
        };
    }

    getChoiceList = () => {
        const { question } = this.props;
        if (question.variables.length > 0) {
            return question.variables.filter((variable) => variable.choiceList.length > 0)[0]
                .choiceList;
        }

        return null;
    };

    validate(): ValidationResultModel {
        if (this.state.feedbackAnswerRequired && isNullOrEmpty(this.state.openEndValue)) {
            return new ValidationBuilder()
                .fail(this.state.feedbackVariable, "IncompleteAnswer", {
                    openEnd: this.state.feedbackVariable,
                })
                .build();
        }
        return new ValidationBuilder().build();
    }

    renderQuestion() {
        return (
            <NetPromoterRenderer
                choices={this.getChoiceList()}
                mentions={this.getMentions(this.props, this.props.sequencedVariables[0].name)}
                onSelect={this.onSelect}
                isDisabled={this.props.isDisabled === true}
                {...getRendererProps(this.props)}
                showFeedbackQuestion={this.state.showFeedbackQuestion}
                feedbackQuestion={this.state.feedbackQuestion}
                feedbackVariable={this.state.feedbackVariable}
                openEndValue={this.state.openEndValue}
                onOpenEndChange={this.onOpenEndChange}
                onOpenEndBlur={this.onOpenEndBlur}
                variable={this.props.sequencedVariables[0]}
            />
        );
    }
}
